import * as THREE from 'three';

class SeededRandom {
    constructor(seed) {
        this.seed = seed;
    }
    next() {
        this.seed = (this.seed * 9301 + 49297) % 233280;
        return this.seed / 233280;
    }
    nextInt(min, max) {
        return Math.floor(this.next() * (max - min)) + min;
    }
};

class WallManager {
    constructor(scene, physics, config, playerRig) {
        this.scene = scene;
        this.physics = physics;
        this.config = config;
        this.playerRig = playerRig;
        this.walls = [];
        this.seed = this.config.walls.seed;
        this.random = new SeededRandom(this.seed);
        this.wallBlockSize = new THREE.Vector3(
            this.config.walls.blockSize.width,
            this.config.walls.blockSize.height,
            this.config.walls.blockSize.depth
        );
        this.wallGenerationWidth = 70; // in blocks
        this.wallCullingDistanceX = (this.wallGenerationWidth / 2 + 10) * this.wallBlockSize.x;
        this.lastSpawnZ = 0;
        this.currentBiomeIndex = 0;
        this.biomeTransitionGap = 0;

        this.cullTick = 0;      // A simple frame counter.
        this.cullInterval = 2;
        this.generationBudgetPerFrame = 2; // OPTIMIZATION: How many wall patterns can be generated in one frame to prevent hitches.
        this.generationsThisFrame = 0;

        this.wallGenerationDepth = 400; // How many units in advance to generate walls
        this.initialSpawnOffsetZ = -0; // Player must travel 200 units before walls appear
        this.spawningHasStarted = false; // State to track if we've passed the offset

        this.time = 0;
        this.reusableColor = new THREE.Color();
        this.colorFunctions = [
            this.colorPatternHSL.bind(this)
        ];
        this.currentColorFunction = this.colorFunctions[0];

        // Instancing setup
        const geometry = new THREE.BoxGeometry(this.wallBlockSize.x, this.wallBlockSize.y, this.wallBlockSize.z);

        const material = new THREE.MeshPhysicalMaterial({
            color: 0xffffff, // Base color, will be overridden by instance colors
        });

        this.instancedMesh = new THREE.InstancedMesh(geometry, material, this.config.walls.maxWalls);
        //this.instancedMesh.instanceColor.setUsage(THREE.DynamicDrawUsage); // For frequent color updates

        this.instancedMesh.castShadow = true;
        this.instancedMesh.receiveShadow = false;
        this.instancedMesh.frustumCulled = false;
        this.scene.add(this.instancedMesh);
        this.dummy = new THREE.Object3D();
        this.reusableBlockMesh = new THREE.Mesh(geometry);

        this.alternatePattern = false; // For alternating patterns
        this.allPatterns = [
            // Biome 0: Basic patterns
            [

                // Checkerboard on the ground
                () => {
                    const width = this.random.nextInt(20, 30);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(false));
                    for (let x = this.alternatePattern ? 1 : 0; x < width; x += 2) {
                        pattern[0][x] = true;
                    }
                    this.alternatePattern = !this.alternatePattern; // Toggle for next call
                    return pattern;
                },

                // Horizontal bars on the ground with openings
                () => {
                    const width = this.random.nextInt(25, 35);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(false));

                    let x = 0;
                    while (x < width) {
                        const segmentLength = this.random.nextInt(4, 8);
                        for (let i = 0; i < segmentLength && x < width; i++, x++) {
                            pattern[0][x] = true; // Place bar on the ground (y=0)
                        }
                        const gapLength = this.random.nextInt(1, 2);
                        x += gapLength;
                    }
                    return pattern;
                },
            ],
            // Biome 1: Harder patterns
            [
                // A wall with a single block opening at the bottom
                () => {
                    const width = this.random.nextInt(2, 5);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(true));
                    const openingX = this.random.nextInt(0, width);
                    pattern[0][openingX] = false; // Opening on the bottom row
                    return pattern;
                },

                () => {
                    const width = this.random.nextInt(15, 25);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(false));

                    let x = 0;
                    while (x < width) {
                        const segmentLength = this.random.nextInt(2, 5);
                        for (let i = 0; i < segmentLength && x < width; i++, x++) {
                            pattern[0][x] = true; // Place bar on the ground (y=0)
                        }
                        const gapLength = this.random.nextInt(1, 3);
                        x += gapLength;
                    }
                    return pattern;
                },

            ],
            // Biome 2: Even harder
            [
                // Tunnel
                () => {
                    const width = this.random.nextInt(5, 10);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(true));
                    const openingWidth = this.random.nextInt(2, 3);
                    const openingHeight = 1;
                    const startX = Math.floor((width - openingWidth) / 2);
                    const startY = 0; // on the ground
                    for (let y = startY; y < startY + openingHeight && y < height; y++) {
                        for (let x = startX; x < startX + openingWidth && x < width; x++) {
                            pattern[y][x] = false;
                        }
                    }
                    return pattern;
                },
                () => {
                    
                    const width = this.random.nextInt(20, 30);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(false));
                    for (let x = this.alternatePattern ? 1 : 0; x < width; x += 2) {
                        pattern[0][x] = true;
                    }
                    return pattern;
                }
            ],
            // Biome 3: Mix and match, very hard
            [
                // Wall with small, offset openings
                () => {
                    const width = this.random.nextInt(5, 10);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(true));
                    pattern[0][this.random.nextInt(0, width)] = false;
                    if (height > 1) {
                        pattern[1][this.random.nextInt(0, width)] = false;
                    }
                    return pattern;
                },
                
                () => {
                    const width = this.random.nextInt(15, 25);
                    const height = this.config.walls.gridHeight;
                    const pattern = Array(height).fill(0).map(() => Array(width).fill(false));

                    let x = 0;
                    while (x < width) {
                        const segmentLength = this.random.nextInt(4, 8);
                        for (let i = 0; i < segmentLength && x < width; i++, x++) {
                            pattern[0][x] = true; // Place bar on the ground (y=0)
                        }
                        const gapLength = this.random.nextInt(2, 3);
                        x += gapLength;
                    }
                    return pattern;
                }
            ]
        ];
    }

    // --- COLOR PATTERN FUNCTIONS ---
    colorPatternSineWave(x, y, z, time) {
        const r = Math.sin(x * 0.1 + time) * 0.5 + 0.5;
        const g = Math.sin(y * 0.1 + time) * 0.5 + 0.5;
        const b = Math.sin(z * 0.1 + time) * 0.5 + 0.5;
        return this.reusableColor.setRGB(r, g, b);
    }

    colorPatternStripes(x, y, z, time) {
        const frequency = 0.2;
        const speed = 0.5;
        const value = Math.sin((y + z) * frequency + time * speed);
        return this.reusableColor.set(value > 0 ? 0xff0000 : 0x0000ff);
    }

    colorPatternHSL(x, y, z, time) {
        const hue = (z * time * 0.1 * 0.01) % 1;
        const saturation = 0.8;
        const lightness = 0.6;
        return this.reusableColor.setHSL(hue, saturation, lightness);
    }


    setBiome(biomeIndex) {
        if (this.currentBiomeIndex !== biomeIndex) {
            this.biomeTransitionGap = 500;
        }
        this.currentBiomeIndex = biomeIndex;
        this.seed = Math.floor(Math.random() * 100000);
        this.random = new SeededRandom(this.seed);
    }

    updateWallColor(color) {
        this.instancedMesh.material.color.set(color);
    }

    startSpawning() {
        this.stopSpawning();
        // No longer uses an interval, spawning is triggered by player movement in update()
    }

    stopSpawning() {
        // No timeout to clear
    }

    spawnWallPattern() {
        const availablePatterns = this.allPatterns[this.currentBiomeIndex] || this.allPatterns[0];
        const patternGenerator = availablePatterns[this.random.nextInt(0, availablePatterns.length)];

        // const availablePatterns = this.allPatterns[3];
        // const patternGenerator = availablePatterns[this.random.nextInt(0, availablePatterns.length)];//availablePatterns[0];

        const pattern = patternGenerator();
        const patternHeight = pattern.length;
        const patternWidth = pattern[0].length;

        const gapBetweenPatterns = 70 + this.biomeTransitionGap;
        this.biomeTransitionGap = 0;

        const startZ = this.lastSpawnZ - gapBetweenPatterns - this.wallBlockSize.z;
        const yOffset = this.wallBlockSize.y / 2 - 2; // Place bottom of walls on the ground plane

        const playerGridX = Math.round(this.playerRig.position.x / this.wallBlockSize.x);
        const startBlockX = playerGridX - Math.floor(this.wallGenerationWidth / 2);
        const endBlockX = playerGridX + Math.floor(this.wallGenerationWidth / 2);

        this.lastSpawnZ = startZ;

        for (let x = startBlockX; x < endBlockX; x++) {
            const patternX = (x % patternWidth + patternWidth) % patternWidth;
            for (let y = 0; y < patternHeight; y++) {
                if (pattern[y][patternX] && this.walls.length < this.config.walls.maxWalls) {
                    const xPos = x * this.wallBlockSize.x;
                    const yPos = y * this.wallBlockSize.y + yOffset;

                    this.reusableBlockMesh.position.set(xPos, yPos, startZ);
                    const wallBody = this.physics.createWall(this.reusableBlockMesh);
                    const instanceId = this.walls.length;
                    this.walls.push({ body: wallBody, instanceId: instanceId });

                    this.dummy.position.copy(this.reusableBlockMesh.position);
                    this.dummy.updateMatrix();
                    this.instancedMesh.setMatrixAt(instanceId, this.dummy.matrix);

                    // --- CHANGE: APPLY COLOR ON CREATION ---
                    const color = this.currentColorFunction(xPos, yPos, startZ, this.time);
                    //this.instancedMesh.setColorAt(instanceId, new THREE.Color(0xffffff));
                }
            }
        }
        this.instancedMesh.instanceMatrix.needsUpdate = true;

        // --- CHANGE: Safely update the color buffer ---
        // if (this.instancedMesh.instanceColor) {
        //     this.instancedMesh.instanceColor.needsUpdate = true;
        // }
        this.instancedMesh.count = this.walls.length;
    }

    removeWall(index) {
        const wallToRemove = this.walls[index];
        const lastWall = this.walls.pop();

        if (index < this.walls.length) {
            this.walls[index] = lastWall;
            this.walls[index].instanceId = index;

            this.dummy.position.copy(lastWall.body.position);
            this.dummy.quaternion.copy(lastWall.body.quaternion);
            this.dummy.updateMatrix();
            this.instancedMesh.setMatrixAt(index, this.dummy.matrix);

            if (this.instancedMesh.instanceColor) {
                this.instancedMesh.getColorAt(lastWall.instanceId, this.reusableColor);
                this.instancedMesh.setColorAt(index, this.reusableColor);
            }
        }

        if (!this.physics.bodiesToRemove.includes(wallToRemove.body)) {
            this.physics.bodiesToRemove.push(wallToRemove.body);
        }
        this.instancedMesh.instanceMatrix.needsUpdate = true;
        if (this.instancedMesh.instanceColor) {
            this.instancedMesh.instanceColor.needsUpdate = true;
        }
    }
    // In WallManager class

    update(deltaTime) {
        this.time += deltaTime;
        for (let i = 0; i < this.walls.length; i++) {
            const wall = this.walls[i];
            const pos = wall.body.position;
            const color = this.currentColorFunction(pos.x, pos.y, pos.z, this.time);
            //this.instancedMesh.setColorAt(i, color);
        }
        // if (this.instancedMesh.instanceColor) {
        //     this.instancedMesh.instanceColor.needsUpdate = true;
        // }

        // OPTIMIZATION: Reset generation count each frame for time-slicing
        this.generationsThisFrame = 0;

        // Spawn new walls if player has moved far enough
        const playerZ = this.playerRig.position.z;

        // Check if the player has traveled past the initial offset to begin spawning
        if (!this.spawningHasStarted && playerZ < this.initialSpawnOffsetZ) {
            this.spawningHasStarted = true;
            // Set the first spawn position relative to where the player is when they cross the threshold
            this.lastSpawnZ = playerZ + this.config.walls.spawnDistance;
        }

        // If spawning is active, continuously generate walls until the desired depth is reached
        if (this.spawningHasStarted) {
            while (this.lastSpawnZ > playerZ - this.wallGenerationDepth) {
                if (this.walls.length >= this.config.walls.maxWalls) break; // Safety break
                // --- OPTIMIZATION: Time-slice wall generation to prevent CPU spikes ---
                if (this.generationsThisFrame >= this.generationBudgetPerFrame) break;
                this.spawnWallPattern();
                this.generationsThisFrame++;
            }
        }

        // --- OPTIMIZATION: Cull less frequently ---
        this.cullTick++;
        if (this.cullTick % this.cullInterval === 0) {
            const playerX = this.playerRig.position.x;

            for (let i = this.walls.length - 1; i >= 0; i--) {
                const wall = this.walls[i];
                let shouldRemove = false;

                // Cull walls that are behind the player
                if (wall.body.position.z > playerZ + 20) {
                    shouldRemove = true;
                }
                // Cull walls that are too far to the side
                if (Math.abs(wall.body.position.x - playerX) > this.wallCullingDistanceX) {
                    shouldRemove = true;
                }

                if (shouldRemove) {
                    this.removeWall(i);
                }
                // Removed redundant matrix update in `else` block for static walls.
            }
        }


        this.instancedMesh.count = this.walls.length;
        // Removed redundant `this.instancedMesh.instanceMatrix.needsUpdate = true;`
        // The flag is now set only when instances are added/removed.
    }

    reset() {
        this.stopSpawning();
        this.walls.forEach(wall => {
            if (!this.physics.bodiesToRemove.includes(wall.body)) {
                this.physics.bodiesToRemove.push(wall.body);
            }
        });
        this.walls = [];
        this.instancedMesh.count = 0;
        this.instancedMesh.instanceMatrix.needsUpdate = true;
        if (this.instancedMesh.instanceColor) {
            this.instancedMesh.instanceColor.needsUpdate = true;
        }
        this.seed = Math.floor(Math.random() * 100000);
        this.random = new SeededRandom(this.seed);
        this.lastSpawnZ = 0;
        this.currentBiomeIndex = 0;
        this.biomeTransitionGap = 0;
        console.log('WallManager reset. New seed:', this.seed);
        this.lastSpawnZ = 0; // Reset to initial Z position
        this.spawningHasStarted = false; // Reset the spawning flag
    }
}
export { WallManager }